/**
 * Firestore compatibility layer using Supabase + JSONB storage.
 * Drop-in replacement for 'firebase/firestore' imports.
 *
 * Storage model: each Firestore collection maps to a Supabase table with
 *   id   TEXT PRIMARY KEY
 *   data JSONB
 * Subcollections (3-segment paths) map to <parent>_<sub> tables with an
 * extra parent_id TEXT column.
 */
import { supabase } from './supabase';

// ─── Internal symbols for special field values ────────────────────────────────

const SERVER_TIMESTAMP = Symbol('serverTimestamp'); // eslint-disable-line no-unused-vars

// ─── Internal helpers ─────────────────────────────────────────────────────────

function _genId() {
  return crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2) + Date.now().toString(36);
}

/** Convert a Firestore collection path to a Supabase table name + optional parentId */
function _parsePath(segments) {
  // segments: ['users'] | ['chamados'] | ['chamados', id, 'mensagens']
  if (segments.length === 1) {
    return { table: segments[0], parentId: null };
  }
  if (segments.length === 3) {
    return { table: `${segments[0]}_${segments[2]}`, parentId: segments[1] };
  }
  // Fallback for other depths
  const table = segments.filter((_, i) => i % 2 === 0).join('_');
  const parentId = segments[segments.length - 2] || null;
  return { table, parentId };
}

/**
 * Process an update object, resolving special markers.
 * Returns { plainUpdates, needsRead } where needsRead signals arrayUnion/Remove/increment.
 */
// Expande "a.b.c" = val dentro de obj, preservando os irmãos existentes
function _setNestedPath(obj, dotPath, value) {
  const parts = dotPath.split('.');
  let cur = obj;
  for (let i = 0; i < parts.length - 1; i++) {
    const part = parts[i];
    if (cur[part] === null || cur[part] === undefined || typeof cur[part] !== 'object' || Array.isArray(cur[part])) {
      cur[part] = {};
    }
    cur = cur[part];
  }
  cur[parts[parts.length - 1]] = value;
}

function _processUpdates(updates) {
  const plain = {};
  const specials = [];

  for (const [key, val] of Object.entries(updates)) {
    if (val && typeof val === 'object') {
      if (val._type === 'serverTimestamp') {
        plain[key] = new Date().toISOString();
      } else if (val._type === 'arrayUnion') {
        specials.push({ key, type: 'arrayUnion', items: val.items });
      } else if (val._type === 'arrayRemove') {
        specials.push({ key, type: 'arrayRemove', item: val.item });
      } else if (val._type === 'increment') {
        specials.push({ key, type: 'increment', n: val.n });
      } else {
        plain[key] = val;
      }
    } else {
      plain[key] = val;
    }
  }
  return { plain, specials };
}

/** Apply Firestore query constraints to a Supabase query builder */
function _applyConstraints(qb, constraints) {
  for (const c of constraints) {
    if (c._type === 'where') {
      qb = _applyWhere(qb, c.field, c.op, c.value);
    } else if (c._type === 'orderBy') {
      qb = qb.order(`data->>${c.field}`, { ascending: c.dir !== 'desc' });
    } else if (c._type === 'limit') {
      qb = qb.limit(c.n);
    }
  }
  return qb;
}

function _applyWhere(qb, field, op, value) {
  const col = `data->>${field}`;

  switch (op) {
    case '==':
      if (value === null) return qb.is(col, null);
      return qb.eq(col, String(value));
    case '!=':
      if (value === null) return qb.not(col, 'is', null);
      return qb.neq(col, String(value));
    case '<':
      return qb.lt(col, String(value));
    case '<=':
      return qb.lte(col, String(value));
    case '>':
      return qb.gt(col, String(value));
    case '>=':
      return qb.gte(col, String(value));
    case 'in':
      return qb.in(col, value.map(String));
    case 'not-in':
      return qb.not(col, 'in', `(${value.map(v => `'${String(v)}'`).join(',')})`);
    case 'array-contains':
      // data->'field' @> '[value]'
      return qb.filter(`data->'${field}'`, 'cs', JSON.stringify([value]));
    case 'array-contains-any': {
      const orClauses = value.map(v => `data->'${field}'.cs.${JSON.stringify([v])}`).join(',');
      return qb.or(orClauses);
    }
    default:
      console.warn(`[firestoreAdapter] Unsupported operator: ${op}`);
      return qb;
  }
}

// O que buscar de cada linha: tudo ('*'), só alguns campos de data (campos(...)) ou
// data calculado por uma função do banco (resumo(...)). Os filtros (where/orderBy)
// continuam valendo sobre o data completo.
function _selectDe(constraints) {
  const c = constraints.find(x => x._type === 'campos');
  if (c) return ['id', ...c.nomes.map(n => `f_${n}:data->${n}`)].join(',');
  const r = constraints.find(x => x._type === 'resumo');
  if (r) return `id,data:${r.funcao}`;
  return '*';
}
function _ajustarLinhas(rows, constraints) {
  const c = constraints.find(x => x._type === 'campos');
  if (!c || !rows) return rows;
  return rows.map(row => {
    const data = {};
    for (const n of c.nomes) if (row[`f_${n}`] !== undefined && row[`f_${n}`] !== null) data[n] = row[`f_${n}`];
    return { id: row.id, data };
  });
}

/** Convert a raw Supabase row into a Firestore-like DocumentSnapshot */
function _makeDocSnapshot(row, table, opts = {}) {
  if (!row) return { exists: () => false, id: null, data: () => null, _fromError: !!opts.fromError };
  const { id, data } = row;
  return {
    id,
    exists: () => true,
    data: () => data,
    get: (field) => data?.[field],
    ref: new DocRef(table, id),
    _fromError: !!opts.fromError,
  };
}

/** Convert an array of Supabase rows into a Firestore-like QuerySnapshot */
function _makeQuerySnapshot(rows, table, opts = {}) {
  const docs = (rows || []).map(row => _makeDocSnapshot(row, table));
  return {
    docs,
    empty: docs.length === 0,
    size: docs.length,
    forEach: (fn) => docs.forEach(fn),
    _fromError: !!opts.fromError,
  };
}

// ─── Reference objects ────────────────────────────────────────────────────────

class CollectionRef {
  constructor(table, parentId = null, constraints = []) {
    this.table = table;
    this.parentId = parentId;
    this.constraints = constraints;
    this._isCollection = true;
  }
}

class DocRef {
  constructor(table, id, parentId = null) {
    this.table = table;
    this.id = id;
    this.parentId = parentId;
    this.path = parentId ? `${table}/${id}` : `${table}/${id}`;
    this._isDoc = true;
  }
}

// ─── Public API ───────────────────────────────────────────────────────────────

/** Firestore db placeholder (not used directly, kept for compatibility) */
export const db = { _isSupabaseAdapter: true };

/** collection(db, 'users') or collection(db, 'chamados', id, 'mensagens') */
export function collection(_db, ...pathSegments) {
  const { table, parentId } = _parsePath(pathSegments);
  return new CollectionRef(table, parentId);
}

/** doc(db, 'users', id) or doc(collectionRef) for auto-ID */
export function doc(_db, ...pathSegments) {
  // doc(collectionRef) — Firestore pattern for auto-ID document inside a collection
  if (_db && _db._isCollection && pathSegments.length === 0) {
    return new DocRef(_db.table, _genId(), _db.parentId);
  }
  if (pathSegments.length === 2) {
    return new DocRef(pathSegments[0], pathSegments[1]);
  }
  if (pathSegments.length === 4) {
    const table = `${pathSegments[0]}_${pathSegments[2]}`;
    return new DocRef(table, pathSegments[3], pathSegments[1]);
  }
  // Single segment — no id yet (used in setDoc where id is in the ref)
  return new DocRef(pathSegments[0], pathSegments[1] || null);
}

/** query(collectionRef, ...constraints) */
export function query(ref, ...constraints) {
  return new CollectionRef(ref.table, ref.parentId, [...(ref.constraints || []), ...constraints]);
}

export function where(field, op, value) {
  return { _type: 'where', field, op, value };
}

export function orderBy(field, dir = 'asc') {
  return { _type: 'orderBy', field, dir };
}

/** Busca só estes campos de cada documento — ex: query(col, where(...), campos('nome', 'convenio')).
 *  Bem mais leve quando a tela não precisa do documento inteiro. Só para leitura:
 *  nunca grave de volta (setDoc sem merge) um documento lido assim. */
export function campos(...nomes) {
  const invalido = nomes.find(n => !/^[A-Za-z0-9_]+$/.test(n));
  if (invalido) throw new Error(`[campos] nome inválido: ${invalido}`);
  return { _type: 'campos', nomes };
}

/** Busca o documento pela função "resumo" do banco em vez do data completo —
 *  ex: resumo('captacao_resumo') (sem as cópias do formulário de áreas no histórico).
 *  Só para leitura/exibição: para editar, busque o documento completo (getDoc). */
export function resumo(funcao) {
  if (!/^[a-z0-9_]+$/.test(funcao)) throw new Error(`[resumo] função inválida: ${funcao}`);
  return { _type: 'resumo', funcao };
}

export function limit(n) {
  return { _type: 'limit', n };
}

export function serverTimestamp() {
  return { _type: 'serverTimestamp' };
}

export function arrayUnion(...items) {
  return { _type: 'arrayUnion', items };
}

export function arrayRemove(item) {
  return { _type: 'arrayRemove', item };
}

export function increment(n) {
  return { _type: 'increment', n };
}

/** Timestamp compatibility shim */
export const Timestamp = {
  fromDate: (date) => ({ toDate: () => date, _seconds: Math.floor(date.getTime() / 1000) }),
  now: () => ({ toDate: () => new Date(), _seconds: Math.floor(Date.now() / 1000) }),
};

// ─── CRUD operations ──────────────────────────────────────────────────────────

// table → Map(parentKey → Set<fn>). Subcoleções (chamados_mensagens, ocorrencias_v2_tarefas,
// chamados_chat, tutoriais_comentarios...) são UMA tabela física compartilhada por TODOS os
// pais (chamados/ocorrências/tutoriais diferentes) — sem esse aninhamento por parentId, uma
// escrita nas tarefas de UMA ocorrência notificava TODAS as assinaturas de tarefas de TODAS
// as ocorrências abertas na tela (achado da varredura de 2026-09-26). _ROOT_KEY é o balde das
// assinaturas de coleção de nível raiz (sem parentId).
const _localListeners = new Map();
const _ROOT_KEY = '__root__';

// 2026-09-26: freio de segurança contra tempestades de refetch — incidente de egress
// (~10.5 milhões de queries em 28h, causando cobrança excessiva no Supabase) mostrou
// que qualquer gatilho (escrita local, evento realtime, poll) chamava os listeners
// direto, sem limite de frequência. Se algum lugar do app entrar num ciclo
// leitura→escrita→leitura, isso virava um loop sem fim, tão rápido quanto o event
// loop permitisse. Agora TODO gatilho passa por aqui, que garante no máximo 1 refetch
// de verdade por (tabela + pai) a cada `_MIN_REFRESH_MS` — gatilhos extras dentro da
// janela só agendam UM refetch de saída (trailing), nunca disparam na hora.
const _MIN_REFRESH_MS = 3000;
const _tableThrottle = new Map(); // "table::parentKey" → { lastFetch: number, pending: boolean }

/**
 * Notifica os onSnapshot inscritos numa tabela. Se `parentId` for passado (string ou null
 * pra coleção raiz), notifica só quem está inscrito NAQUELE pai específico — não a tabela
 * inteira. Se `parentId` for omitido (undefined), notifica todo mundo (usado pelo poll de
 * segurança, que não sabe qual linha mudou).
 */
function _notifyLocalListeners(table, parentId) {
  const parentMap = _localListeners.get(table);
  if (!parentMap) return;
  const scoped = parentId !== undefined;
  const key = scoped ? `${table}::${parentId ?? _ROOT_KEY}` : table;

  const fire = () => {
    const t = _tableThrottle.get(key);
    if (t) { t.lastFetch = Date.now(); t.pending = false; }
    if (scoped) {
      parentMap.get(parentId ?? _ROOT_KEY)?.forEach(fn => fn());
    } else {
      for (const set of parentMap.values()) set.forEach(fn => fn());
    }
  };

  let t = _tableThrottle.get(key);
  if (!t) { t = { lastFetch: 0, pending: false }; _tableThrottle.set(key, t); }
  const elapsed = Date.now() - t.lastFetch;
  if (elapsed >= _MIN_REFRESH_MS) {
    fire();
  } else if (!t.pending) {
    t.pending = true;
    setTimeout(fire, _MIN_REFRESH_MS - elapsed);
  }
  // se já existe um refetch pendente pra essa chave, esse gatilho não precisa fazer nada —
  // o refetch de saída já vai pegar o estado mais recente quando disparar.
}

/** Força re-fetch imediato de todos os onSnapshot que escutam esta tabela (todos os pais).
 *  Útil após escritas feitas server-side (ex: FTP sync) que não passam pelo adapter. */
export function invalidateTable(table) {
  _notifyLocalListeners(table);
}

// Auto-cria a tabela no Supabase se não existir (equivalente ao Firebase que cria collections automaticamente)
async function _ensureTable(table) {
  try {
    await supabase.rpc('create_collection_table', { p_table: table });
  } catch (e) {
    console.warn('[ensureTable] Não foi possível criar tabela', table, e?.message);
  }
}

function _isMissingTable(error) {
  const msg = (error?.message || '').toLowerCase();
  return msg.includes('does not exist') || msg.includes('relation') || msg.includes('undefined_table') || msg.includes('schema cache') || msg.includes('could not find the table');
}

// Garante que nenhuma query fique pendurada para sempre (ex: chave inválida ou timeout de rede)
function _withTimeout(promise, ms, label) {
  return new Promise((resolve, reject) => {
    const t = setTimeout(() => reject(new Error(`[Supabase timeout] ${label} — ${ms / 1000}s sem resposta`)), ms);
    promise.then(r => { clearTimeout(t); resolve(r); }, e => { clearTimeout(t); reject(e); });
  });
}

export async function getDocs(ref) {
  const { table, parentId, constraints = [] } = ref;
  // 2026-10-06: 15s estourava em consultas legítimas e grandes (ex: lista de
  // captação ativa, ~46MB) em conexões mais lentas — o servidor respondia
  // certo, só demorava mais pra baixar+processar que o limite permitia, e a
  // tela caía silenciosamente em "vazio" (fail-open). 60s dá folga real sem
  // deixar a tela travada pra sempre se o servidor cair de verdade.
  const QUERY_TIMEOUT = 60000;

  try {
    const hasLimit = constraints.some(c => c._type === 'limit');
    const sel = _selectDe(constraints);
    if (hasLimit) {
      let qb = supabase.from(table).select(sel);
      if (parentId) qb = qb.eq('parent_id', parentId);
      qb = _applyConstraints(qb, constraints);
      const { data, error } = await _withTimeout(qb, QUERY_TIMEOUT, table);
      if (error && _isMissingTable(error)) { await _ensureTable(table); return _makeQuerySnapshot([], table, { fromError: true }); }
      if (error) throw new Error(`[getDocs] ${table}: ${error.message}`);
      return _makeQuerySnapshot(_ajustarLinhas(data, constraints), table);
    }

    let allData = [];
    let start = 0;
    const pageSize = 1000;
    const hasOrderBy = constraints.some(c => c._type === 'orderBy');

    while (true) {
      let qb = supabase.from(table).select(sel).range(start, start + pageSize - 1);
      if (parentId) qb = qb.eq('parent_id', parentId);
      qb = _applyConstraints(qb, constraints);
      if (!hasOrderBy) qb = qb.order('id', { ascending: true });

      const { data, error } = await _withTimeout(qb, QUERY_TIMEOUT, table);
      if (error && _isMissingTable(error)) { await _ensureTable(table); return _makeQuerySnapshot([], table, { fromError: true }); }
      if (error) throw new Error(`[getDocs AutoPaginate] ${table}: ${error.message}`);

      if (!data || data.length === 0) break;
      allData = allData.concat(_ajustarLinhas(data, constraints));
      if (data.length < pageSize) break;
      start += pageSize;
    }

    return _makeQuerySnapshot(allData, table);
  } catch (e) {
    // Timeout or network error — return empty snapshot instead of crashing the app.
    // The query will retry on the next polling cycle or user interaction.
    // Tagged as fromError so onSnapshot listeners don't mistake this for a real
    // empty result (which was resetting "already seen" baselines and causing
    // mass notification bursts once the next poll succeeded).
    console.warn(`[getDocs] "${table}" inacessível: ${e.message}`);
    return _makeQuerySnapshot([], table, { fromError: true });
  }
}

/** Conta documentos que batem com a query SEM baixar o conteúdo deles — usa
 *  HEAD request (0 bytes de corpo na resposta), ideal pra badges/contadores que
 *  só precisam saber "quantos", não os dados em si. */
export async function getCountFromServer(ref) {
  const { table, parentId, constraints = [] } = ref;
  try {
    let qb = supabase.from(table).select('*', { count: 'exact', head: true });
    if (parentId) qb = qb.eq('parent_id', parentId);
    qb = _applyConstraints(qb, constraints.filter(c => c._type !== 'orderBy' && c._type !== 'limit'));
    const { count, error } = await _withTimeout(qb, 15000, `getCount ${table}`);
    if (error && _isMissingTable(error)) { await _ensureTable(table); return 0; }
    if (error) throw new Error(`[getCountFromServer] ${table}: ${error.message}`);
    return count || 0;
  } catch (e) {
    console.warn(`[getCountFromServer] "${table}" inacessível: ${e.message}`);
    return 0;
  }
}

export async function getDoc(docRef) {
  const { table, id } = docRef;
  try {
    const { data, error } = await _withTimeout(
      supabase.from(table).select('*').eq('id', id).maybeSingle(),
      15000, `getDoc ${table}/${id}`
    );
    if (error) throw new Error(`[getDoc] ${table}/${id}: ${error.message}`);
    return _makeDocSnapshot(data, table);
  } catch (e) {
    console.warn(`[getDoc] "${table}/${id}" inacessível: ${e.message}`);
    return _makeDocSnapshot(null, table, { fromError: true });
  }
}

export async function addDoc(collectionRef, rawData) {
  const { table, parentId } = collectionRef;
  const id = _genId();
  const data = _flattenData(rawData);
  const row = { id, data };
  if (parentId) row.parent_id = parentId;
  let { error } = await supabase.from(table).insert(row);
  if (error && _isMissingTable(error)) {
    await _ensureTable(table);
    const retry = await supabase.from(table).insert(row);
    error = retry.error;
  }
  if (error) throw new Error(`[addDoc] ${table}: ${error.message}`);
  _notifyLocalListeners(table, parentId ?? null);
  return { id, path: `${table}/${id}` };
}

export async function setDoc(docRef, rawData, options = {}) {
  const { table, id, parentId } = docRef;
  const data = _flattenData(rawData);
  if (options.merge) {
    let { data: existing, error: fetchErr } = await supabase.from(table).select('data').eq('id', id).maybeSingle();
    if (fetchErr && _isMissingTable(fetchErr)) {
      // Tabela ainda não existe nesse projeto Supabase (ex: user_presence) — cria e tenta de novo,
      // mesmo mecanismo já usado por getDocs/addDoc, em vez de falhar toda vez.
      await _ensureTable(table);
      ({ data: existing, error: fetchErr } = await supabase.from(table).select('data').eq('id', id).maybeSingle());
    }
    if (fetchErr) throw fetchErr;
    const merged = { ...(existing?.data || {}), ..._resolverMarcadoresDeLista(data, existing?.data || {}) };
    const row = { id, data: merged };
    if (parentId) row.parent_id = parentId;
    let { error } = await supabase.from(table).upsert(row, { onConflict: 'id' });
    if (error && _isMissingTable(error)) {
      await _ensureTable(table);
      ({ error } = await supabase.from(table).upsert(row, { onConflict: 'id' }));
    }
    if (error) throw new Error(`[setDoc/merge] ${table}/${id}: ${error.message}`);
    _notifyLocalListeners(table, parentId ?? null);
    return;
  } else {
    const row = { id, data: _resolverMarcadoresDeLista(data) };
    if (parentId) row.parent_id = parentId;
    let { error } = await supabase.from(table).upsert(row, { onConflict: 'id' });
    if (error && _isMissingTable(error)) {
      await _ensureTable(table);
      ({ error } = await supabase.from(table).upsert(row, { onConflict: 'id' }));
    }
    if (error) throw new Error(`[setDoc] ${table}/${id}: ${error.message}`);
  }
  _notifyLocalListeners(table, parentId ?? null);
}

export async function updateDoc(docRef, updates) {
  const { table, id, parentId } = docRef;
  const { plain, specials } = _processUpdates(updates);

  // Read-modify-write: always needed to merge correctly
  const { data: existing, error: fetchErr } = await supabase.from(table).select('data').eq('id', id).maybeSingle();
  if (fetchErr) throw fetchErr;
  const merged = { ...(existing?.data || {}) };

  // Apply plain fields — expandindo notação ponto ("a.b.c") para nested objects
  for (const [key, val] of Object.entries(plain)) {
    _setNestedPath(merged, key, val);
  }

  // Apply specials (arrayUnion/arrayRemove/increment), também com suporte a notação ponto
  for (const spec of specials) {
    const parts = spec.key.split('.');
    let cur = merged;
    for (let i = 0; i < parts.length - 1; i++) {
      if (!cur[parts[i]] || typeof cur[parts[i]] !== 'object') cur[parts[i]] = {};
      cur = cur[parts[i]];
    }
    const leaf = parts[parts.length - 1];
    if (spec.type === 'arrayUnion') {
      const arr = Array.isArray(cur[leaf]) ? cur[leaf] : [];
      for (const item of spec.items) {
        if (!arr.some(a => JSON.stringify(a) === JSON.stringify(item))) arr.push(item);
      }
      cur[leaf] = arr;
    } else if (spec.type === 'arrayRemove') {
      const arr = Array.isArray(cur[leaf]) ? cur[leaf] : [];
      cur[leaf] = arr.filter(a => JSON.stringify(a) !== JSON.stringify(spec.item));
    } else if (spec.type === 'increment') {
      cur[leaf] = (Number(cur[leaf]) || 0) + spec.n;
    }
  }

  const { error } = await supabase.from(table).update({ data: merged }).eq('id', id);
  if (error) throw new Error(`[updateDoc] ${table}/${id}: ${error.message}`);
  _notifyLocalListeners(table, parentId ?? null);
}

export async function deleteDoc(docRef) {
  const { table, id, parentId } = docRef;
  const { error } = await supabase.from(table).delete().eq('id', id);
  if (error) throw new Error(`[deleteDoc] ${table}/${id}: ${error.message}`);
  _notifyLocalListeners(table, parentId ?? null);
}

// ─── Real-time listener ───────────────────────────────────────────────────────
// 2026-09-23: passou a usar WebSocket/Realtime (postgres_changes) como gatilho —
// em vez de perguntar "mudou?" a cada ~2min pra sempre, só refaz a busca quando o
// Postgres avisa que uma linha da tabela realmente mudou. O poll por tempo virou
// uma rede de segurança (a cada ~10min), pro caso do WebSocket cair e perder algum
// evento. Isso só funciona nas tabelas com replicação ligada (rodar uma vez, por
// tabela: ALTER PUBLICATION supabase_realtime ADD TABLE nome_da_tabela;). Nas que
// ainda não têm, cai sozinho no comportamento antigo (poll de segurança), sem
// quebrar nada — dá pra ligar tabela por tabela, sem pressa.

// Timer compartilhado por tabela: em vez de N timers (um por subscriber),
// um único timer por tabela chama todos os subscribers de uma vez.
const _tableTimers = new Map(); // table → { timer, count }
const _realtimeChannels = new Map(); // table → RealtimeChannel

function _ensureRealtimeChannel(table) {
  if (_realtimeChannels.has(table)) return;
  const channel = supabase
    .channel(`rt:${table}`)
    .on('postgres_changes', { event: '*', schema: 'public', table }, (payload) => {
      // Extrai o parent_id da linha que mudou pra só notificar quem está olhando
      // ESSE pai (ex: só quem tem a ocorrência X aberta, não todas as ocorrências
      // que compartilham a mesma tabela física de "tarefas"). Linhas sem parent_id
      // (tabelas de nível raiz) caem em null, que é o balde da coleção raiz.
      const parentId = payload?.new?.parent_id ?? payload?.old?.parent_id ?? null;
      _notifyLocalListeners(table, parentId); // passa pelo freio de _MIN_REFRESH_MS — nunca dispara na hora sem limite
    })
    .subscribe((status) => {
      if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
        console.warn(`[realtime] "${table}" sem WebSocket (replicação não ligada ainda?) — seguindo só com o poll de segurança.`);
      }
    });
  _realtimeChannels.set(table, channel);
}

export function onSnapshot(refOrQuery, callbackOrOptions, maybeCallback) {
  const callback = typeof callbackOrOptions === 'function' ? callbackOrOptions : maybeCallback;
  const ref = refOrQuery;
  const { table, parentId } = ref;
  const parentKey = parentId ?? _ROOT_KEY;

  let isMounted = true;
  let everSucceeded = false;

  async function fetchAndNotify() {
    if (!isMounted) return;
    try {
      const snap = ref._isDoc ? await getDoc(ref) : await getDocs(ref);
      if (!isMounted) return;
      // getDoc/getDocs never reject — network/timeout errors come back as an empty
      // snapshot tagged _fromError. If we already have real data, skip delivering
      // this one instead of overwriting the caller's state with a false "empty"
      // result (that was resetting onSnapshot-based "already seen" baselines and
      // causing mass notification bursts once the next poll succeeded).
      if (snap._fromError && everSucceeded) return;
      everSucceeded = true;
      callback(snap);
    } catch (e) {
      if (_isMissingTable(e)) {
        await _ensureTable(table);
        if (isMounted && !everSucceeded) {
          callback(ref._isDoc ? _makeDocSnapshot(null, ref.table || '') : _makeQuerySnapshot([], ref.table || ''));
        }
        return;
      }
      console.error('[onSnapshot] fetch error:', e.message);
      if (isMounted && !everSucceeded) {
        callback(ref._isDoc ? _makeDocSnapshot(null, ref.table || '') : _makeQuerySnapshot([], ref.table || ''));
      }
    }
  }

  // Listener local — notificado imediatamente em escritas na mesma sessão, e
  // também em eventos de realtime (quando a tabela tem replicação ligada).
  // Aninhado por parentKey pra escritas numa subcoleção (ex: tarefas da ocorrência X)
  // não notificarem quem está olhando a subcoleção de outro pai (ocorrência Y).
  if (!_localListeners.has(table)) _localListeners.set(table, new Map());
  const _parentMap = _localListeners.get(table);
  if (!_parentMap.has(parentKey)) _parentMap.set(parentKey, new Set());
  _parentMap.get(parentKey).add(fetchAndNotify);
  _ensureRealtimeChannel(table);

  // Busca inicial
  fetchAndNotify();

  _prenderTimerDaTabela(table);

  // Ao voltar para a aba, recarrega imediatamente
  function onVisible() {
    if (document.visibilityState === 'visible' && isMounted) fetchAndNotify();
  }
  document.addEventListener('visibilitychange', onVisible);

  return function unsubscribe() {
    isMounted = false;
    document.removeEventListener('visibilitychange', onVisible);
    const parentSet = _localListeners.get(table)?.get(parentKey);
    parentSet?.delete(fetchAndNotify);
    if (parentSet && parentSet.size === 0) _localListeners.get(table)?.delete(parentKey);
    _soltarTimerDaTabela(table);
  };
}

// Timer compartilhado por tabela: agora é só rede de segurança (10min + jitter)
// pro caso do WebSocket cair e perder algum evento — deixou de ser o mecanismo
// principal de atualização nas tabelas com replicação ligada.
function _prenderTimerDaTabela(table) {
  if (!_tableTimers.has(table)) {
    const POLL_MS = 600000;
    const jitter = Math.floor(Math.random() * 60000); // 0-60s spread
    const timer = setInterval(() => {
      if (document.visibilityState !== 'hidden') {
        _notifyLocalListeners(table);
      }
    }, POLL_MS + jitter);
    _tableTimers.set(table, { timer, count: 0 });
  }
  _tableTimers.get(table).count++;
}

// Para o timer compartilhado e fecha o canal realtime quando não há mais subscribers
function _soltarTimerDaTabela(table) {
  const entry = _tableTimers.get(table);
  if (!entry) return;
  entry.count--;
  if (entry.count <= 0) {
    clearInterval(entry.timer);
    _tableTimers.delete(table);
    const channel = _realtimeChannels.get(table);
    if (channel) {
      supabase.removeChannel(channel);
      _realtimeChannels.delete(table);
    }
  }
}

/**
 * Escuta a mesma subcoleção de VÁRIOS pais com uma assinatura só — ex: as tarefas de
 * todas as ocorrências da tela: onSnapshotDeVariosPais('ocorrencias_v2', 'tarefas', ids, cb).
 *
 * 2026-10-15: a tela de Ocorrências abria um onSnapshot por ocorrência (551 na visão de
 * setor), e cada um refazia a busca sozinho a cada poll de segurança e a cada volta para a
 * aba — ~147 mil consultas em 8h. Aqui a carga é feita em lotes de 50 pais; uma mudança
 * num pai (escrita local ou realtime) rebusca só aquele pai; o poll de segurança e a volta
 * para a aba (só se ficou mais de 1 min fora) rebuscam todos, em lote.
 *
 * callback({ [parentId]: [{ id, ...data }] }) recebe só os pais que foram recarregados.
 * Em erro de rede não chama o callback (a tela continua com o que já tinha).
 */
export function onSnapshotDeVariosPais(parentCollection, subCollection, parentIds, callback) {
  const { table } = _parsePath([parentCollection, '_', subCollection]);
  const LOTE = 50;
  const PAGINA = 1000;
  let isMounted = true;
  const pendentes = new Set();
  let agendado = null;

  async function buscar(ids) {
    const porPai = {};
    ids.forEach(id => { porPai[id] = []; });
    for (let i = 0; i < ids.length; i += LOTE) {
      const lote = ids.slice(i, i + LOTE);
      for (let start = 0; ; start += PAGINA) {
        const { data, error } = await _withTimeout(
          supabase.from(table).select('*').in('parent_id', lote).order('id').range(start, start + PAGINA - 1),
          60000, table
        );
        if (error) throw new Error(`[onSnapshotDeVariosPais] ${table}: ${error.message}`);
        (data || []).forEach(r => porPai[r.parent_id]?.push({ id: r.id, ...r.data }));
        if (!data || data.length < PAGINA) break;
      }
    }
    return porPai;
  }

  // Junta os pedidos que chegam juntos (ex: o poll de segurança chama um por pai) numa busca só
  function agendar(parentId) {
    if (parentId === undefined) parentIds.forEach(id => pendentes.add(id));
    else pendentes.add(parentId);
    if (agendado) return;
    agendado = setTimeout(async () => {
      agendado = null;
      const ids = [...pendentes];
      pendentes.clear();
      if (!isMounted || !ids.length) return;
      try {
        const porPai = await buscar(ids);
        if (isMounted) callback(porPai);
      } catch (e) {
        console.warn(`[onSnapshotDeVariosPais] "${table}" inacessível: ${e.message}`);
      }
    }, 200);
  }

  if (!_localListeners.has(table)) _localListeners.set(table, new Map());
  const parentMap = _localListeners.get(table);
  const ouvintes = parentIds.map(id => {
    const fn = () => agendar(id);
    if (!parentMap.has(id)) parentMap.set(id, new Set());
    parentMap.get(id).add(fn);
    return [id, fn];
  });
  _ensureRealtimeChannel(table);
  _prenderTimerDaTabela(table);

  agendar();

  let escondidoEm = null;
  function onVisibility() {
    if (document.visibilityState === 'hidden') { escondidoEm = Date.now(); return; }
    if (escondidoEm && Date.now() - escondidoEm >= 60000) agendar();
    escondidoEm = null;
  }
  document.addEventListener('visibilitychange', onVisibility);

  return function unsubscribe() {
    isMounted = false;
    if (agendado) clearTimeout(agendado);
    document.removeEventListener('visibilitychange', onVisibility);
    ouvintes.forEach(([id, fn]) => {
      const set = parentMap.get(id);
      set?.delete(fn);
      if (set && set.size === 0) parentMap.delete(id);
    });
    _soltarTimerDaTabela(table);
  };
}

// ─── Batch writes (non-atomic — sequential execution) ────────────────────────

export function writeBatch() {
  const ops = [];
  return {
    set(docRef, data, options) {
      ops.push(() => setDoc(docRef, data, options || {}));
    },
    update(docRef, data) {
      ops.push(() => updateDoc(docRef, data));
    },
    delete(docRef) {
      ops.push(() => deleteDoc(docRef));
    },
    async commit() {
      for (const op of ops) await op();
    },
  };
}

// ─── Transaction (basic — no retry, no isolation) ────────────────────────────

export async function runTransaction(_db, fn) {
  const tx = {
    get: getDoc,
    set: setDoc,
    update: updateDoc,
    delete: deleteDoc,
  };
  return fn(tx);
}

// ─── Firestore helpers ────────────────────────────────────────────────────────

function _flattenData(raw) {
  const out = {};
  for (const [k, v] of Object.entries(raw)) {
    if (v && typeof v === 'object' && v._type === 'serverTimestamp') {
      out[k] = new Date().toISOString();
    } else if (v && typeof v === 'object' && typeof v.toDate === 'function') {
      // Timestamp.now()/fromDate() shim: a real Date wrapped in a non-JSON-serializable
      // toDate() closure. Sem isso, o campo "toDate" é descartado ao gravar no Supabase
      // (JSON.stringify remove funções) e a leitura posterior perde a data original.
      out[k] = v.toDate().toISOString();
    } else {
      out[k] = v;
    }
  }
  return out;
}

// arrayUnion/arrayRemove dentro de setDoc: sem isto o marcador era gravado como objeto
// ({ _type: 'arrayUnion', items }) em vez de lista. Com merge, combina com o que já existe.
function _resolverMarcadoresDeLista(data, existente = {}) {
  const out = { ...data };
  for (const [k, v] of Object.entries(out)) {
    if (!v || typeof v !== 'object') continue;
    const atual = Array.isArray(existente[k]) ? existente[k] : [];
    if (v._type === 'arrayUnion') {
      const lista = [...atual];
      for (const item of v.items || []) if (!lista.some(a => JSON.stringify(a) === JSON.stringify(item))) lista.push(item);
      out[k] = lista;
    } else if (v._type === 'arrayRemove') {
      out[k] = atual.filter(a => JSON.stringify(a) !== JSON.stringify(v.item));
    }
  }
  return out;
}
